/**
 * Group Booking utilities.
 *
 * Allow multiple people to join a booking together.
 * Each group member pays their share separately.
 *
 * Group config in listing.publicData.groupBooking = {
 *   enabled: boolean,
 *   minGroupSize: number,  — default 2
 *   maxGroupSize: number,  — default 10
 *   pricePerPerson: boolean — if true, price is per-person (not per-booking)
 * }
 *
 * Group booking state in transaction.protectedData.group = {
 *   size: number,
 *   members: [{ userId, name, status: 'pending'|'confirmed'|'declined' }]
 * }
 */

export const DEFAULT_MIN_GROUP = 2;
export const DEFAULT_MAX_GROUP = 10;

/**
 * Get group booking config from listing.
 * @param {Object|null} listing
 * @returns {Object|null}
 */
export const getGroupConfig = (listing) => {
  const config = listing?.attributes?.publicData?.groupBooking;
  if (!config?.enabled) return null;
  return config;
};

/**
 * Calculate total price for a group booking.
 * @param {number} basePriceCents — per-booking or per-person price
 * @param {boolean} pricePerPerson
 * @param {number} groupSize
 * @returns {{ totalCents: number, perPersonCents: number }}
 */
export const calculateGroupPrice = (basePriceCents, pricePerPerson, groupSize) => {
  const size = Math.max(1, groupSize);
  const totalCents = pricePerPerson ? basePriceCents * size : basePriceCents;
  const perPersonCents = Math.round(totalCents / size);
  return { totalCents, perPersonCents };
};

/**
 * Validate group size.
 * @param {number} size
 * @param {Object} config
 * @returns {string|null}
 */
export const validateGroupSize = (size, config) => {
  const min = config?.minGroupSize || DEFAULT_MIN_GROUP;
  const max = config?.maxGroupSize || DEFAULT_MAX_GROUP;
  if (size < min) return `Group must have at least ${min} people`;
  if (size > max) return `Group can have at most ${max} people`;
  return null;
};

/**
 * Get confirmed member count.
 * @param {Object[]} members
 * @returns {number}
 */
export const getConfirmedCount = (members) =>
  Array.isArray(members) ? members.filter(m => m.status === 'confirmed').length : 0;
